import { useState, useEffect } from "react";
import { Menu, X, ChevronRight, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { SearchDialog } from "@/components/SearchDialog";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cynergy-homepage-v2/artifacts/r1v9vkwo_cynergysports_logo_full.png";

const NAV_LINKS = [
  { label: "Sports", href: "/sports" },
  { label: "Travel", href: "/travel" },
  { label: "Interviews", href: "/interviews" },
  { label: "Clubs", href: "/clubs" },
  { label: "Meets", href: "/meets" },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  // On homepage, header starts transparent over hero then becomes white on scroll
  // On inside pages, header is always white
  const showLight = !isHome || scrolled;

  const openSearch = () => {
    setMobileOpen(false);
    setSearchOpen(true);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showLight
          ? "bg-white shadow-[0_1px_0_0_rgba(186,117,23,0.12)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-[72px]">
        {/* Logo */}
        <Link to="/" data-testid="header-logo-link" className="flex-shrink-0">
          <img
            src={LOGO_URL}
            alt="CynergySports - sports for all"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav data-testid="desktop-nav" className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((link) => {
            const isActive = location.pathname === link.href;
            return (
              <Link
                key={link.label}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase()}-link`}
                className={`font-inter text-[15px] font-medium tracking-wide transition-colors duration-150 relative group ${
                  showLight
                    ? isActive
                      ? "text-amber-dusk"
                      : "text-near-black/70 hover:text-amber-dusk"
                    : isActive
                      ? "text-solar-orange"
                      : "text-white/80 hover:text-solar-orange"
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-[2px] transition-all duration-200 ${
                    isActive
                      ? "w-full bg-amber-dusk"
                      : "w-0 bg-solar-orange group-hover:w-full"
                  }`}
                />
              </Link>
            );
          })}
        </nav>

        {/* Search */}
        <button
          type="button"
          data-testid="header-search-button"
          className={`hidden md:inline-flex items-center gap-2 rounded-sm border px-4 py-2.5 font-inter text-sm font-medium transition-colors duration-150 ${
            showLight
              ? "border-near-black/10 bg-white text-near-black hover:border-amber-dusk/30 hover:text-amber-dusk"
              : "border-white/25 bg-white/10 text-white hover:border-solar-orange/50 hover:text-solar-orange"
          }`}
          onClick={openSearch}
          aria-label="Open site search"
        >
          <Search size={16} />
          Search
        </button>

        {/* Desktop CTA */}
        <Link
          to="/sport-news"
          data-testid="header-cta-button"
          className="hidden md:inline-flex items-center gap-2 bg-solar-orange text-near-black font-inter font-semibold text-sm px-6 py-2.5 rounded-sm hover:bg-amber-dusk hover:text-white transition-colors duration-150"
        >
          Latest Sports News
          <ChevronRight size={16} />
        </Link>

        {/* Mobile Toggle */}
        <div className="flex items-center gap-1 md:hidden">
          <button
            type="button"
            data-testid="mobile-search-button"
            className={`p-2 ${showLight ? "text-near-black" : "text-white"}`}
            onClick={openSearch}
            aria-label="Open site search"
          >
            <Search size={22} />
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className={`p-2 ${showLight ? "text-near-black" : "text-white"}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-white border-t border-amber-dusk/10 overflow-hidden shadow-lg"
          >
            <nav className="flex flex-col px-6 py-6 gap-4">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}-link`}
                  className={`font-inter text-lg font-medium transition-colors duration-150 ${
                    location.pathname === link.href
                      ? "text-amber-dusk"
                      : "text-near-black/70 hover:text-amber-dusk"
                  }`}
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/sport-news"
                data-testid="mobile-cta-button"
                className="inline-flex items-center justify-center gap-2 bg-solar-orange text-near-black font-inter font-semibold text-sm px-6 py-3 rounded-sm mt-2"
                onClick={() => setMobileOpen(false)}
              >
                Latest Sports News
                <ChevronRight size={16} />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}
